import React from 'react';
import { Box, makeStyles } from '@material-ui/core';

const STATUS_CONFIG = {
  synced: {
    label: 'Synced',
    tone: 'green',
    dot: 'filled',
  },
  publishing: {
    label: 'Publishing…',
    tone: 'blue',
    dot: 'pulse',
  },
  branch_creation_scheduled: {
    label: 'Branch creation scheduled',
    tone: 'blue',
    dot: 'hollow',
  },
  publish_failed: {
    label: 'Publish failed',
    tone: 'red',
    dot: 'filled',
  },
  no_content_live: {
    label: 'No content live',
    tone: 'red',
    dot: 'filled',
  },
  needs_publish: {
    label: 'Needs publish',
    tone: 'amber',
    dot: 'hollow',
  },
  legacy_studio: {
    label: 'Legacy Studio',
    tone: 'grey',
    dot: 'filled',
  },
  status_unavailable: {
    label: 'Status unavailable',
    tone: 'grey',
    dot: 'filled',
  },
  none_assigned: {
    label: 'None assigned',
    tone: 'grey',
    dot: 'filled',
  },
};

const useStyles = makeStyles({
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: 10,
    boxSizing: 'border-box',
    // Equal inset around content (top/bottom and left/right), matching prototype pills.
    padding: '4px 10px',
    gap: 6,
    borderRadius: 999,
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.01em',
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    pointerEvents: 'none',
    userSelect: 'none',
    boxShadow: 'none',
    border: '1px solid transparent',
  },
  green: {
    color: '#2f6b3a',
    backgroundColor: '#eaf5ec',
    borderColor: '#c5dfc9',
  },
  blue: {
    color: '#1e5a96',
    backgroundColor: '#eaf2fb',
    borderColor: '#c5d8ef',
  },
  red: {
    color: '#a33b3b',
    backgroundColor: '#f9eaea',
    borderColor: '#e8c5c5',
  },
  amber: {
    color: '#8a6d1d',
    backgroundColor: '#f8f1de',
    borderColor: '#e6d7a8',
  },
  grey: {
    color: '#6b6b6b',
    backgroundColor: '#f3f3f3',
    borderColor: '#d9d9d9',
  },
  loading: {
    color: 'transparent',
    backgroundColor: '#ececec',
    borderColor: '#e0e0e0',
    minWidth: 96,
    position: 'relative',
    overflow: 'hidden',
    animation: '$shimmer 1.4s ease-in-out infinite',
  },
  dot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    flexShrink: 0,
    backgroundColor: 'currentColor',
  },
  hollow: {
    backgroundColor: 'transparent',
    border: '1.5px solid currentColor',
    boxSizing: 'border-box',
  },
  pulse: {
    animation: '$pulse 1.6s ease-in-out infinite',
  },
  loadingDot: {
    backgroundColor: '#d0d0d0',
  },
  '@keyframes pulse': {
    '0%': { opacity: 1, transform: 'scale(1)' },
    '50%': { opacity: 0.4, transform: 'scale(0.85)' },
    '100%': { opacity: 1, transform: 'scale(1)' },
  },
  '@keyframes shimmer': {
    '0%': { opacity: 0.65 },
    '50%': { opacity: 1 },
    '100%': { opacity: 0.65 },
  },
});

/**
 * Renders publication.status from the backend. No business-state logic.
 * Soft inline status pill (not a button). Shows a skeleton while loading.
 */
const CoursePublicationChip = ({ status, loading = false }) => {
  const classes = useStyles();

  if (loading) {
    return (
      <Box
        component="span"
        className={`${classes.chip} ${classes.loading}`}
        aria-label="Loading publication status"
        aria-busy="true"
      >
        <span
          className={`${classes.dot} ${classes.loadingDot}`}
          aria-hidden="true"
        />
        Loading
      </Box>
    );
  }

  const config = STATUS_CONFIG[status];
  if (!config) {
    return null;
  }

  const dotClass = [
    classes.dot,
    config.dot === 'hollow' ? classes.hollow : '',
    config.dot === 'pulse' ? classes.pulse : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Box
      component="span"
      className={`${classes.chip} ${classes[config.tone]}`}
      aria-label={config.label}
    >
      <span className={dotClass} aria-hidden="true" />
      {config.label}
    </Box>
  );
};

export default CoursePublicationChip;
